import {
  SITE_URL,
  BRAND_NAME,
  LEGAL_NAME,
  ALTERNATE_NAME,
  ORG_DESCRIPTION,
  ORG_COUNTRY,
  ORG_ID,
  ORG_LOGO,
  WEBSITE_ID,
  SOCIAL_URLS,
  CONTACT_EMAIL,
  FOUNDING_DATE,
  FOUNDERS,
  founderByName,
  type Founder,
} from "./seo-constants";

// One standalone Person node per founder — referenced by @id from
// Organization.founder and (for the content author) from article author.
export function people() {
  return FOUNDERS.map((f: Founder) => ({
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": f.id,
    name: f.name,
    jobTitle: f.title,
    worksFor: { "@type": "Organization", "@id": ORG_ID },
    url: f.url ?? SITE_URL,
    ...(f.sameAs?.length ? { sameAs: f.sameAs } : {}),
    ...(f.bio ? { description: f.bio } : {}),
  }));
}

export function serializeJsonLd(data: unknown): string {
  return JSON.stringify(data).replace(/</g, "\\u003c").replace(/>/g, "\\u003e");
}

export function organization() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORG_ID,
    name: BRAND_NAME,
    legalName: LEGAL_NAME,
    alternateName: ALTERNATE_NAME,
    description: ORG_DESCRIPTION,
    url: SITE_URL,
    logo: ORG_LOGO,
    foundingDate: FOUNDING_DATE,
    address: {
      "@type": "PostalAddress",
      addressCountry: ORG_COUNTRY,
    },
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "customer support",
        email: CONTACT_EMAIL,
        availableLanguage: ["en"],
      },
    ],
    founder: FOUNDERS.map((f) => ({ "@type": "Person", "@id": f.id, name: f.name })),
    sameAs: SOCIAL_URLS,
  };
}

export function website() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": WEBSITE_ID,
    name: BRAND_NAME,
    url: SITE_URL,
    inLanguage: "en",
  };
}

export function softwareApplication(opts: {
  id?: string;
  name?: string;
  description?: string;
  offers?: { name: string; price?: number; priceCurrency?: string; url: string }[];
}) {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "@id": opts.id ?? `${SITE_URL}/#axy-app`,
    name: opts.name ?? BRAND_NAME,
    description:
      opts.description ??
      "Axy.digital is the autonomous algorithmic marketing engine that detects market demand and turns it into GEO, LinkedIn, and X campaigns automatically.",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    offers: opts.offers?.map((o) => ({
      "@type": "Offer",
      name: o.name,
      ...(o.price !== undefined && { price: o.price }),
      ...(o.priceCurrency !== undefined && { priceCurrency: o.priceCurrency }),
      url: o.url,
      availability: "https://schema.org/InStock",
    })),
    publisher: { "@type": "Organization", "@id": ORG_ID },
  };
}

export function product(opts: {
  name: string;
  description: string;
  url: string;
  offers?: { name: string; price?: number; priceCurrency?: string }[];
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: opts.name,
    description: opts.description,
    url: opts.url,
    brand: { "@type": "Organization", "@id": ORG_ID },
    offers: opts.offers?.map((o) => ({
      "@type": "Offer",
      name: o.name,
      ...(o.price !== undefined && { price: o.price }),
      ...(o.priceCurrency !== undefined && { priceCurrency: o.priceCurrency }),
      availability: "https://schema.org/InStock",
      url: opts.url,
    })),
  };
}

type PortableTextSpan = { text?: string };
type PortableTextBlock = { _type?: string; children?: PortableTextSpan[] };

// `wordCount` for Article nodes: counts the words of every Portable Text `block`
// (headings and paragraphs), ignoring images, embeds and other custom types.
export function portableTextWordCount(body: unknown): number {
  if (!Array.isArray(body)) return 0;
  return (body as PortableTextBlock[])
    .filter((node) => node?._type === "block")
    .flatMap((node) => node.children ?? [])
    .map((span) => span?.text ?? "")
    .join(" ")
    .split(/\s+/)
    .filter(Boolean).length;
}

// `author` and `publisher` are bare @id references: __root.tsx already emits one
// Person node per founder plus the Organization on every page, so re-declaring
// them here would create competing nodes for the same identity. An author string
// that resolves to no founder has no such node to point at, so it stays inline.
// `authorOrg` bylines the Organization instead of a Person — case studies are
// written by the company, not by an individual.
export function article(opts: {
  headline: string;
  description?: string;
  url: string;
  image?: string | object[];
  datePublished?: string;
  dateModified?: string;
  authorName?: string;
  authorOrg?: boolean;
  articleSection?: string;
  keywords?: string[];
  wordCount?: number;
  type?: "Article" | "BlogPosting" | "TechArticle" | "NewsArticle";
}) {
  const author = founderByName(opts.authorName);
  const isKnownAuthor =
    !opts.authorName || author.name.toLowerCase() === opts.authorName.trim().toLowerCase();
  const image = Array.isArray(opts.image) && !opts.image.length ? undefined : opts.image;
  const authorNode = opts.authorOrg
    ? { "@id": ORG_ID }
    : isKnownAuthor
      ? { "@id": author.id }
      : { "@type": "Person", name: opts.authorName };

  return {
    "@context": "https://schema.org",
    "@type": opts.type ?? "Article",
    "@id": `${opts.url}#article`,
    headline: opts.headline,
    ...(opts.description ? { description: opts.description } : {}),
    url: opts.url,
    mainEntityOfPage: { "@type": "WebPage", "@id": opts.url },
    isPartOf: { "@id": WEBSITE_ID },
    ...(image ? { image } : {}),
    ...(opts.datePublished ? { datePublished: opts.datePublished } : {}),
    ...(opts.dateModified ? { dateModified: opts.dateModified } : {}),
    author: authorNode,
    publisher: { "@id": ORG_ID },
    ...(opts.articleSection ? { articleSection: opts.articleSection } : {}),
    ...(opts.wordCount ? { wordCount: opts.wordCount } : {}),
    ...(opts.keywords?.length ? { keywords: opts.keywords.join(", ") } : {}),
    inLanguage: "en",
  };
}

export function breadcrumb(items: { name: string; url: string }[]): object {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: item.url.startsWith("http") ? item.url : `${SITE_URL}${item.url}`,
    })),
  };
}

// Testimonials → schema.org Review nodes. Each review is tied to the product
// being reviewed via `itemReviewedId` (defaults to the Axy.digital SoftwareApplication),
// so AI can attribute quote → author → company → product reliably.
export function reviews(
  items: {
    quote: string;
    name: string;
    role?: string;
    company?: string;
    rating?: number;
  }[],
  opts?: { itemReviewedId?: string; itemReviewedName?: string },
): object[] {
  const itemReviewedId = opts?.itemReviewedId ?? `${SITE_URL}/#axy-app`;
  const itemReviewedName = opts?.itemReviewedName ?? BRAND_NAME;
  return items.map((r) => ({
    "@context": "https://schema.org",
    "@type": "Review",
    reviewBody: r.quote,
    author: {
      "@type": "Person",
      name: r.name,
      ...(r.role || r.company ? { jobTitle: [r.role, r.company].filter(Boolean).join(", ") } : {}),
      ...(r.company ? { worksFor: { "@type": "Organization", name: r.company } } : {}),
    },
    itemReviewed: {
      "@type": "SoftwareApplication",
      "@id": itemReviewedId,
      name: itemReviewedName,
    },
    reviewRating: {
      "@type": "Rating",
      ratingValue: r.rating ?? 5,
      bestRating: 5,
      worstRating: 1,
    },
    publisher: { "@type": "Organization", "@id": ORG_ID },
  }));
}

export function faqPage(items: { q: string; a: string }[]): object {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function howTo(opts: {
  name: string;
  description?: string;
  steps: { name: string; text: string }[];
}): object {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: opts.name,
    ...(opts.description ? { description: opts.description } : {}),
    step: opts.steps.map((s) => ({
      "@type": "HowToStep",
      name: s.name,
      text: s.text,
    })),
  };
}

export function webPage(opts: {
  name: string;
  description?: string;
  url: string;
  datePublished?: string;
  dateModified?: string;
}): object {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: opts.name,
    ...(opts.description ? { description: opts.description } : {}),
    url: opts.url,
    ...(opts.datePublished ? { datePublished: opts.datePublished } : {}),
    ...(opts.dateModified ? { dateModified: opts.dateModified } : {}),
    publisher: { "@type": "Organization", "@id": ORG_ID },
  };
}

export function collectionPage(opts: {
  name: string;
  description?: string;
  url: string;
  items?: { name: string; url: string }[];
}): object {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: opts.name,
    ...(opts.description ? { description: opts.description } : {}),
    url: opts.url,
    publisher: { "@type": "Organization", "@id": ORG_ID },
    ...(opts.items?.length
      ? {
          hasPart: opts.items.map((it) => ({
            "@type": "WebPage",
            name: it.name,
            url: it.url.startsWith("http") ? it.url : `${SITE_URL}${it.url}`,
          })),
        }
      : {}),
  };
}

export function aboutPage(opts: { name: string; description?: string; url: string }): object {
  return {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: opts.name,
    ...(opts.description ? { description: opts.description } : {}),
    url: opts.url,
    mainEntity: { "@type": "Organization", "@id": ORG_ID },
  };
}
